// Pure HTTP transport: wraps fetch, returns a typed SuccessResponse, throws
// ApiFetchError on non-2xx. Knows nothing about auth policy; protected
// endpoints should wrap this via protectedFetch.

import { HttpStatus, SuccessResponse } from '@airhead-games/shared/types';
import { getBaseUrl } from '@airhead-games/shared/utilities';

export interface BaseFetchOptions<TReq = unknown> {
  method?: string;
  body?: TReq;
  token?: string;
  params?: Record<string, string | string[]>;
  signal?: AbortSignal;
  credentials?: RequestCredentials; // 'include' | 'same-origin' | 'omit'
  /** Override the API origin for a narrowly scoped transport client. */
  apiOrigin?: string;
}

export class ApiFetchError extends Error {
  status: number;
  statusText: string;
  response: unknown;
  constructor(
    message: string,
    status: number,
    statusText: string,
    response: unknown
  ) {
    super(message);
    this.status = status;
    this.statusText = statusText;
    this.response = response;
  }
}

type SuccessHttpStatus =
  | HttpStatus.OK
  | HttpStatus.CREATED
  | HttpStatus.ACCEPTED
  | HttpStatus.NO_CONTENT;

const isSuccessHttpStatus = (status: number): status is SuccessHttpStatus => {
  switch (status) {
    case HttpStatus.OK:
    case HttpStatus.CREATED:
    case HttpStatus.ACCEPTED:
    case HttpStatus.NO_CONTENT:
      return true;
    default:
      return false;
  }
};

export async function baseFetch<TReq = unknown, TRes = unknown>(
  path: string,
  options: BaseFetchOptions<TReq> = {}
): Promise<SuccessResponse<TRes>> {
  const {
    method = 'GET',
    body,
    params,
    signal,
    credentials = 'include',
    apiOrigin = getBaseUrl(),
  } = options;

  const url = new URL(`${apiOrigin}/api${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((val) => url.searchParams.append(k, val));
      } else {
        url.searchParams.append(k, v);
      }
    });
  }

  const headers: Record<string, string> = {};

  const methodUpper = method.toUpperCase();
  const methodsWithBody = ['POST', 'PUT', 'PATCH', 'DELETE'];
  const fetchOptions: RequestInit = {
    method,
    headers,
    credentials,
    signal,
  };
  if (body && methodsWithBody.includes(methodUpper)) {
    headers['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(body);
  }

  const res = await fetch(url.toString(), fetchOptions);

  let data: unknown;
  const contentType = res.headers.get('Content-Type') || '';
  try {
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      data = await res.text();
    }
  } catch {
    throw new ApiFetchError(
      'Invalid response from server',
      res.status,
      res.statusText,
      null
    );
  }

  if (!res.ok) {
    const errorBody = data as { error?: { message?: string } };
    throw new ApiFetchError(
      errorBody?.error?.message || 'Request failed',
      res.status,
      res.statusText,
      data
    );
  }

  if (!isSuccessHttpStatus(res.status)) {
    throw new ApiFetchError(
      'Unsupported successful response status',
      res.status,
      res.statusText,
      data
    );
  }

  return {
    success: true,
    data: data as TRes,
    status: res.status,
  };
}
